import { useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree, extend } from "@react-three/fiber";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

extend({ OrbitControls });

const CAMERA = { position: [0, 0.35, 3.6], fov: 40 };
const GL = { alpha: true, antialias: true };
const KEY_LIGHT = [4, 6, 5];
const FILL_LIGHT = [-3, 2, 4];

function Controls({ autoRotate = true }) {
  const { camera, gl } = useThree();
  const ref = useRef();
  const args = useMemo(() => [camera, gl.domElement], [camera, gl]);
  useFrame(() => ref.current && ref.current.update());
  return (
    <orbitControls
      ref={ref}
      args={args}
      enablePan={false}
      autoRotate={autoRotate}
      autoRotateSpeed={1.6}
      enableDamping
      dampingFactor={0.08}
      minDistance={1.8}
      maxDistance={6}
    />
  );
}

function FitCamera() {
  const { camera, size } = useThree();
  useEffect(() => {
    const aspect = size.width / Math.max(size.height, 1);
    camera.position.z = 3.6 / Math.min(1, aspect);
    camera.updateProjectionMatrix();
  }, [camera, size]);
  return null;
}

function Environment() {
  const { gl, scene } = useThree();
  useEffect(() => {
    const pmrem = new THREE.PMREMGenerator(gl);
    const env = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    scene.environment = env;
    return () => {
      scene.environment = null;
      env.dispose();
      pmrem.dispose();
    };
  }, [gl, scene]);
  return null;
}

// Loads the GLB manually (not via Suspense) so we can report real byte-progress through `onProgress` and surface a
// load failure to the parent via `onError` (which falls back to the 2D render image).
function Figurine({ url, headers, spin, underside, onProgress, onLoaded, onError }) {
  const [gltf, setGltf] = useState(null);
  const ref = useRef();

  useEffect(() => {
    let cancelled = false;
    setGltf(null);
    onProgress?.(0);
    const loader = new GLTFLoader();
    if (headers) loader.setRequestHeader(headers);
    loader.load(
      url,
      (g) => {
        if (cancelled) return;
        setGltf(g);
        onLoaded?.();
      },
      (e) => {
        if (cancelled || !e) return;
        if (e.lengthComputable && e.total) onProgress?.(Math.min(99, Math.round((e.loaded / e.total) * 100)));
      },
      (err) => {
        if (!cancelled) onError?.(err);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const object = useMemo(() => {
    if (!gltf) return null;
    const root = gltf.scene.clone(true);
    const box = new THREE.Box3().setFromObject(root);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const s = 2.2 / Math.max(size.x, size.y, size.z, 1e-6);
    // underside preview: centre the duck and tip it towards the viewer, exactly like turning the printed duck over in
    // your hand — so the painted base (and its mirrored QR) faces the camera and reads the way it prints.
    root.position.set(-center.x * s, underside ? -center.y * s : -box.min.y * s - 1.05, -center.z * s);
    root.scale.setScalar(s);
    root.traverse((o) => {
      if (!o.isMesh || !o.material) return;
      o.material = o.material.clone(); // matte printed-PLA look instead of the default shiny glTF material
      o.material.metalness = 0;
      o.material.roughness = 0.92;
      o.material.envMapIntensity = 0.3;
      if (o.material.specularIntensity !== undefined) o.material.specularIntensity = 0.08;
      o.material.needsUpdate = true;
    });
    const holder = new THREE.Group();
    holder.add(root);
    if (underside) holder.rotation.x = -Math.PI / 2;
    return holder;
  }, [gltf, underside]);

  useFrame((_, dt) => {
    if (spin && !underside && ref.current) ref.current.rotation.y += dt * 0.45;
  });

  if (!object) return null;
  return <primitive ref={ref} object={object} />;
}

export const DuckScene = ({ url, headers, spin = true, underside = false, onProgress, onLoaded, onError }) => (
  <Canvas camera={CAMERA} gl={GL} onCreated={({ gl }) => gl.domElement.addEventListener("webglcontextlost", () => onError?.(new Error("context lost")))}>
    <ambientLight intensity={0.55} />
    <directionalLight position={KEY_LIGHT} intensity={0.7} />
    <directionalLight position={FILL_LIGHT} intensity={0.35} />
    <Environment />
    <FitCamera />
    <Figurine url={url} headers={headers} spin={spin} underside={underside} onProgress={onProgress} onLoaded={onLoaded} onError={onError} />
    <Controls autoRotate={!underside} />
  </Canvas>
);
